'use client';

import React, {
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useState,
} from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { useVariantSelection } from './VariantSelectionContext';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import AutoScroll from 'embla-carousel-auto-scroll';
import styles from './ImageGallery.module.css';
// import { useWindowSize } from 'hooks/dom/useWindowSize';

const ImageGallery: React.FC = () => {
  const { variantData } = useVariantSelection();
  const autoScrollRef = useRef<ReturnType<typeof AutoScroll> | null>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const [isCursorVisible, setIsCursorVisible] = useState(false);

  const plugins = useMemo(() => {
    const autoScroll = AutoScroll({
      playOnInit: true,
      stopOnFocusIn: true,
      stopOnMouseEnter: true,
      speed: 1,
    });
    autoScrollRef.current = autoScroll;
    return [autoScroll];
  }, []);

  const emblaOptions = useMemo(
    () => ({
      loop: true,
      slidesToScroll: 1,
      dragFree: true,
    }),
    [],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions, plugins);

  useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit(emblaOptions);

      const pluginsObj = emblaApi.plugins();
      const autoScrollPlugin = (pluginsObj as any)?.autoScroll;
      if (autoScrollPlugin) {
        autoScrollRef.current = autoScrollPlugin;
      }
    }
  }, [emblaApi, emblaOptions]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (cursorRef.current) {
      cursorRef.current.style.left = `${e.clientX}px`;
      cursorRef.current.style.top = `${e.clientY}px`;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsCursorVisible(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsCursorVisible(false);

    if (!autoScrollRef.current) return;

    const plugin = autoScrollRef.current as any;
    if (typeof plugin.play === 'function') {
      try {
        plugin.play();
      } catch (err) {
        console.warn('Failed to resume auto-scroll:', err);
      }
    } else if (typeof plugin.resume === 'function') {
      try {
        plugin.resume();
      } catch (err) {
        console.warn('Failed to resume auto-scroll:', err);
      }
    } else if (typeof plugin.start === 'function') {
      try {
        plugin.start();
      } catch (err) {
        console.warn('Failed to resume auto-scroll:', err);
      }
    }
  }, []);

  const getImageUrl = useCallback((path: string) => {
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    return getCompressedAssetUrl(path);
  }, []);

  if (!variantData.galleryImages || variantData.galleryImages.length === 0) {
    return null;
  }

  return (
    <section className={styles.section}>
      <div
        className={styles.emblaContainer}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className={styles.emblaViewport} ref={emblaRef}>
          <div className={styles.emblaContainerInner}>
            {variantData.galleryImages.map((imagePath, index) => (
              <div className={styles.emblaSlide} key={`${imagePath}-${index}`}>
                <div className={styles.imageWrapper}>
                  <img
                    className={styles.galleryImage}
                    src={getImageUrl(imagePath)}
                    alt={`Gallery image ${index + 1}`}
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          className={`${styles.customCursor}${
            isCursorVisible ? ' visible' : ''
          }`}
          ref={cursorRef}
        >
          DRAG
        </div>
      </div>
    </section>
  );
};

export default ImageGallery;
